import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, formatMoney, formatDate } from "@/lib/api";
import { displayFor, backendToUiStatus, uiToBackendStatus, countNotCleared } from "@/lib/bills-util";
import { projectIncomeOccurrences } from "@/lib/incomes-util";
import { useBillsBadge } from "@/lib/bills-badge-context";
import { toast } from "sonner";
import {
  Trash2, RefreshCw, CalendarClock, ArrowLeft,
} from "lucide-react";
import RescheduleBillModal from "@/components/RescheduleBillModal";

// Bills page — this file owns the top-level state, filters, and page layout.
// Sub-components (tables, cards, modals, dialogs, page header) live in
// `./bills/*`. Do NOT re-inline them here — keeping them small is what makes
// future changes to one flow (edit modal, scope prompt, mobile card, etc.)
// safe and localized.
import { FILTERS, monthLabel, computePayPeriodRanges } from "./bills/utils";
import { BulkActionsToolbar } from "./bills/BulkActionsToolbar";
import { PayPeriodFooter } from "./bills/PayPeriodFooter";
import { ChooseDeleteScopeModal } from "./bills/ChooseDeleteScopeModal";
import { SplitBillModal } from "./bills/SplitBillModal";
import { CarryoverPanel } from "./bills/CarryoverPanel";
import { MonthTable } from "./bills/MonthTable";
import { MonthStripNav } from "./bills/MonthStripNav";
import { BillModal } from "./bills/BillModal";
import { BillsPageHeader } from "./bills/BillsPageHeader";
import { HORIZONS, HorizonToggle, HorizonSummary } from "./bills/HorizonToggle";

// localStorage key for the last horizon the user picked. Sticky across
// sessions so a user who prefers "By month" gets it every time.
const HORIZON_STORAGE_KEY = "wmrb_bills_horizon";
// localStorage key for the sort order inside a rolling-day horizon view.
// Values: "due" (default, chronological) | "amount" (biggest first).
const HORIZON_SORT_KEY = "wmrb_bills_horizon_sort";

export default function Bills() {
  const [searchParams] = useSearchParams();
  // Deep-link filter — read once on mount, then let the user drive via the
  // filter dropdown. Powers the "Overdue" / "Pending" alert links in the
  // Dashboard's Reconcile Card so clicking a count opens the Bills page
  // already scoped to just those rows. Whitelist derives from FILTERS so
  // new options stay in sync automatically.
  const initialFilter = (() => {
    const f = searchParams.get("filter");
    if (f && FILTERS.some((x) => x.key === f)) return f;
    return "all";
  })();
  const navigate = useNavigate();
  const { refresh: refreshBillsBadge } = useBillsBadge();
  const payPeriodFilter = searchParams.get("payPeriod");
  // Optional ad-hoc date range filter — used by the Dashboard "Looking
  // further out" and "Next three months" cards so clicking a Bills total
  // there jumps straight into a focused subset of Bills for that window.
  const rangeFromFilter = searchParams.get("from");
  const rangeToFilter = searchParams.get("to");
  const [bills, setBills] = useState([]);
  const [fundingSources, setFundingSources] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  // Upcoming income within the horizon window is included on the "cash
  // available" side of the cushion math, so users don't see a false
  // shortfall when their paychecks cover the gap.
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  // Filter by which funding source (account) the bill is paid from.
  // "all" = all bills, "__unassigned__" = bills with no funding source set,
  // otherwise a funding source id.
  const [accountFilter, setAccountFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [splitting, setSplitting] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllPayPeriod, setSelectAllPayPeriod] = useState(false);
  // Which single month is being viewed (YYYY-MM string), or null = default "This + Next"
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  // Horizon mode — which rolling-day window (or the by-month view) to show.
  // Persisted to localStorage so the user's preference sticks between visits.
  // Default is "14" (Next 2 weeks) since that maps to the most-common
  // "what's hitting me before my next paycheck?" question.
  const [horizonMode, setHorizonMode] = useState(() => {
    try {
      const saved = window.localStorage.getItem(HORIZON_STORAGE_KEY);
      if (saved && HORIZONS.some((h) => h.key === saved)) return saved;
    } catch { /* ignore */ }
    return "14";
  });
  const setHorizonModePersistent = (v) => {
    setHorizonMode(v);
    try { window.localStorage.setItem(HORIZON_STORAGE_KEY, v); } catch { /* ignore */ }
    // Leaving the by-month view clears any selected month chip so the user
    // doesn't see a stale filter carrying over between modes.
    if (v !== "month") setSelectedMonthKey(null);
  };
  // Sort order inside the rolling-day horizon view. Default "due" keeps the
  // chronological register feel; "amount" surfaces the biggest hits first
  // for users who want to prioritise by size.
  const [horizonSort, setHorizonSort] = useState(() => {
    try {
      const saved = window.localStorage.getItem(HORIZON_SORT_KEY);
      if (saved === "due" || saved === "amount") return saved;
    } catch { /* ignore */ }
    return "due";
  });
  const setHorizonSortPersistent = (v) => {
    setHorizonSort(v);
    try { window.localStorage.setItem(HORIZON_SORT_KEY, v); } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    const [b, fs, pp, inc] = await Promise.all([
      api.get("/bills"),
      api.get("/funding-sources"),
      api.get("/forecast/pay-periods"),
      api.get("/incomes"),
    ]);
    setBills(b.data);
    setFundingSources(fs.data);
    setPayPeriods(pp.data);
    setIncomes(inc.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const today = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0); return t;
  }, []);

  // Month boundaries derived from `today` — memoized so they keep stable
  // references across renders. Without this, downstream `.filter()` calls
  // over 200+ bills would re-run on every keystroke, tab switch, or
  // status update. (Feb 2026 perf pass.)
  const currentMonthStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today],
  );
  const nextMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + 1, 1),
    [today],
  );
  const monthAfter = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + 2, 1),
    [today],
  );
  const ranges = useMemo(() => computePayPeriodRanges(payPeriods, today), [payPeriods, today]);

  const passesFilter = (b) => {
    // Account (funding source) filter is independent of the status filter.
    if (accountFilter !== "all") {
      if (accountFilter === "__unassigned__") {
        if (b.funding_source_id) return false;
      } else if (b.funding_source_id !== accountFilter) {
        return false;
      }
    }
    if (filter === "all") return true;
    if (filter === "autopay") return b.is_autopay;
    if (filter === "recurring") return b.is_recurring;
    const d = displayFor(b, today);
    if (filter === "overdue") {
      // Unpaid AND due date has already passed.
      if (d.col !== "unpaid") return false;
      const due = new Date(b.due_date + "T00:00:00");
      return due < today;
    }
    if (filter === "unpaid") return d.col === "unpaid";
    if (filter === "not-cleared") return d.notCleared;
    if (filter === "cleared") return d.col === "paid" && !d.notCleared;
    return true;
  };

  // Carryover = unpaid bills whose due_date is BEFORE this month's start
  const carryoverBills = useMemo(() =>
    bills
      .filter((b) => {
        if (["paid", "cleared"].includes(b.status)) return false;
        const d = new Date(b.due_date + "T00:00:00");
        return d < currentMonthStart;
      })
      .sort((a, b) => a.due_date.localeCompare(b.due_date)),
    [bills, currentMonthStart]);

  const carryoverTotal = carryoverBills.reduce(
    (s, b) => s + Math.max(0, Number(b.estimated_due_amount) - Number(b.paid_amount || 0)),
    0
  );

  const filteredBills = useMemo(
    () => bills.filter(passesFilter),
    [bills, filter, accountFilter, today, passesFilter],
  );

  const groupByRange = (rangeStart, rangeEnd) =>
    filteredBills
      .filter((b) => {
        const d = new Date(b.due_date + "T00:00:00");
        return d >= rangeStart && d < rangeEnd;
      })
      .sort((a, b) => a.due_date.localeCompare(b.due_date));

  const thisMonthBills = useMemo(
    () => groupByRange(currentMonthStart, nextMonth),
    [filteredBills, currentMonthStart, nextMonth],
  );
  // Build 12 months of chip data for the top strip (past months only).
  // "Next month" is folded into the leftmost "Current Month" chip because
  // the default register view already stacks This + Next together.
  // Order: leftmost = current, then Jun, May, Apr … going back 12 months.
  const monthChips = useMemo(() => {
    const arr = [];
    for (let offset = -1; offset >= -12; offset--) {
      const start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
      const end = new Date(today.getFullYear(), today.getMonth() + offset + 1, 1);
      const monthBills = bills.filter((b) => {
        const d = new Date(b.due_date + "T00:00:00");
        return d >= start && d < end;
      });
      const total = monthBills.reduce((s, b) => s + Number(b.estimated_due_amount), 0);
      const paidTotal = monthBills.reduce((s, b) => {
        const disp = displayFor(b, today);
        return s + (disp.col === "paid" && !disp.notCleared ? Number(b.estimated_due_amount) : 0);
      }, 0);
      const unpaidTotal = total - paidTotal;
      const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
      arr.push({
        key, offset,
        label: start.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
        shortLabel: start.toLocaleDateString("en-US", { month: "short" }),
        count: monthBills.length,
        total, paidTotal, unpaidTotal,
        isPast: true,
        isCurrent: false,
        isNext: false,
        start, end,
      });
    }
    return arr;
  }, [bills, today]);

  const selectedChip = selectedMonthKey ? monthChips.find((c) => c.key === selectedMonthKey) : null;
  const selectedMonthBills = selectedChip
    ? filteredBills
        .filter((b) => {
          const d = new Date(b.due_date + "T00:00:00");
          return d >= selectedChip.start && d < selectedChip.end;
        })
        .sort((a, b) => a.due_date.localeCompare(b.due_date))
    : [];
  const nextMonthBills = useMemo(
    () => groupByRange(nextMonth, monthAfter),
    [filteredBills, nextMonth, monthAfter],
  );
  const otherMonthBills = useMemo(
    () => filteredBills
      .filter((b) => {
        const d = new Date(b.due_date + "T00:00:00");
        return d >= monthAfter;
      })
      .sort((a, b) => a.due_date.localeCompare(b.due_date)),
    [filteredBills, monthAfter],
  );

  // Rolling-horizon window: everything due from TODAY through today + N days
  // (inclusive), plus any still-unpaid bills whose due_date is in the past
  // (past due). Past-due rows are surfaced here because they're the most
  // urgent thing the user needs to see, no matter which rolling window they
  // picked. Sorted by due_date ascending so overdue floats to the top.
  const activeHorizon = HORIZONS.find((h) => h.key === horizonMode);
  const horizonDays = activeHorizon?.days ?? null;
  const horizonEnd = useMemo(() => {
    if (!horizonDays) return null;
    const end = new Date(today);
    end.setDate(end.getDate() + horizonDays);
    return end;
  }, [today, horizonDays]);
  const horizonBills = useMemo(() => {
    if (!horizonDays) return [];
    const filtered = filteredBills.filter((b) => {
      const d = new Date(b.due_date + "T00:00:00");
      const isPastDue = d < today && !["paid", "cleared"].includes(b.status);
      const inWindow = d >= today && d <= horizonEnd;
      return isPastDue || inWindow;
    });
    if (horizonSort === "amount") {
      // Biggest bills first. Past-due bills still surface via colour
      // treatment inside the row; users who chose "sort by amount"
      // explicitly want money-magnitude order, so we honour that here.
      return filtered.sort(
        (a, b) => Number(b.estimated_due_amount || 0) - Number(a.estimated_due_amount || 0)
      );
    }
    return filtered.sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [filteredBills, today, horizonEnd, horizonDays, horizonSort]);
  const horizonPastDue = horizonBills.filter((b) => {
    const d = new Date(b.due_date + "T00:00:00");
    return d < today && !["paid", "cleared"].includes(b.status);
  });
  const horizonPastDueTotal = horizonPastDue.reduce(
    (s, b) => s + Number(b.estimated_due_amount || 0), 0
  );
  // Unpaid dollars owed in the current horizon window — used to trigger the
  // cushion warning when this exceeds available cash.
  const horizonUnpaidTotal = horizonBills
    .filter((b) => !["paid", "cleared"].includes(b.status))
    .reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
  // Available cash across all "spending" funding sources (checking/cash the
  // user marked as contributing to their total balance). Matches the
  // Dashboard's Step-1 "Real bank balance" number so the warning threshold
  // stays consistent across the two screens.
  const availableCash = fundingSources
    .filter((f) => f.contributes_to_total_balance && f.current_source_balance_optional != null)
    .reduce((s, f) => s + Number(f.current_source_balance_optional || 0), 0);
  // Expected income arriving inside the same rolling horizon window —
  // paychecks and any other income row with an occurrence between today
  // and (today + horizonDays) that hasn't been marked received yet. Uses
  // `projectIncomeOccurrences` so RECURRING paychecks (weekly / biweekly /
  // monthly) whose anchor `expected_date` is outside the window still
  // count when a projected occurrence lands inside it. Fixes the cushion
  // warning missing next-week's biweekly paycheck (Feb 2026).
  const horizonIncoming = useMemo(() => {
    if (!horizonDays) return 0;
    const startBoundary = today;
    const endBoundary = horizonEnd;
    return incomes
      .filter((i) => i.status !== "received" && i.expected_date)
      .flatMap((i) => projectIncomeOccurrences(i, startBoundary, endBoundary))
      .reduce((s, i) => s + Number(i.amount || 0), 0);
  }, [incomes, today, horizonEnd, horizonDays]);
  const totalAvailableInWindow = availableCash + horizonIncoming;
  const cushionShortfall = horizonUnpaidTotal - totalAvailableInWindow;
  const showCushionWarning = horizonMode !== "month" && cushionShortfall > 0 && horizonUnpaidTotal > 0;

  // Recently paid — last 30 days of bills the user marked paid or cleared.
  // Useful on mobile where the user quickly wants to check "did I already
  // pay Netflix this month?" without tapping into By-month view. Sorted
  // newest-first because that's how recall usually flows ("what did I
  // pay this week?").
  const recentlyPaidBills = useMemo(() => {
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return filteredBills
      .filter((b) => ["paid", "cleared"].includes(b.status))
      .filter((b) => {
        const d = new Date(b.due_date + "T00:00:00");
        return d >= thirtyDaysAgo && d <= today;
      })
      .sort((a, b) => b.due_date.localeCompare(a.due_date));
  }, [filteredBills, today]);
  const recentlyPaidTotal = recentlyPaidBills.reduce(
    (s, b) => s + Number(b.estimated_due_amount || 0), 0
  );
  const [showRecentlyPaid, setShowRecentlyPaid] = useState(false);

  // If ?payPeriod=YYYY-MM-DD is set, we render a focused subset instead of the
  // regular This-month / Next-month / Other-month tables. The window is looked
  // up from /forecast/pay-periods so the filter matches exactly what the
  // Dashboard's "Accumulated bills (N)" line was showing.
  const activePayPeriod = useMemo(() => {
    if (!payPeriodFilter) return null;
    return payPeriods.find((p) => p.pay_date === payPeriodFilter) || null;
  }, [payPeriodFilter, payPeriods]);

  const payPeriodBills = useMemo(() => {
    if (!activePayPeriod) return [];
    const start = activePayPeriod.period_start;
    const end = activePayPeriod.period_end;
    return filteredBills
      .filter((b) => b.due_date >= start && b.due_date <= end)
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [activePayPeriod, filteredBills]);

  // Synthesized "active range" — mirrors activePayPeriod shape so downstream
  // rendering can use one code path. Only kicks in when ?from=&to= is set
  // AND no ?payPeriod= is set (payPeriod takes precedence).
  const activeDateRange = useMemo(() => {
    if (payPeriodFilter) return null;
    if (!rangeFromFilter || !rangeToFilter) return null;
    return { period_start: rangeFromFilter, period_end: rangeToFilter };
  }, [payPeriodFilter, rangeFromFilter, rangeToFilter]);

  const dateRangeBills = useMemo(() => {
    if (!activeDateRange) return [];
    const start = activeDateRange.period_start;
    const end = activeDateRange.period_end;
    return filteredBills
      .filter((b) => b.due_date >= start && b.due_date <= end)
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [activeDateRange, filteredBills]);

  // Scroll-preserving in-place state update after any bill PATCH
  const applyUpdatedBill = (updated) => {
    setBills((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
    // Also refresh the sidebar Not-Cleared badge since status may have shifted.
    refreshBillsBadge();
  };

  // Refetch the bills list. Used after any edit that may cascade to other
  // same-name recurring rows (name, amount, funding source, autopay, pay url,
  // notes, frequency) so the whole chain reflects the change in the UI, not
  // only the row the user directly touched.
  const refreshBills = async () => {
    try {
      const { data } = await api.get("/bills");
      setBills(data);
      refreshBillsBadge();
    } catch { /* ignore — next full load will catch up */ }
  };

  const setStatus = async (bill, status) => {
    try {
      const upd = { status };
      if (status === "paid" || status === "cleared" || status === "pending") {
        upd.paid_amount = bill.estimated_due_amount;
      } else if (status === "unpaid") {
        upd.paid_amount = 0;
      }
      const { data } = await api.patch(`/bills/${bill.id}`, upd);
      applyUpdatedBill(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateAmount = async (bill, newAmount) => {
    const amount = Number(newAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (amount === Number(bill.estimated_due_amount)) return;
    try {
      const { data } = await api.patch(`/bills/${bill.id}`, { estimated_due_amount: amount });
      applyUpdatedBill(data);
      // Amount cascades to future unpaid rows for recurring bills — refetch
      // so the whole chain reflects the new amount, not just the edited row.
      if (bill.is_recurring) refreshBills();
      toast.success(`Updated ${bill.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // When set to a bill, opens the "which delete?" modal for recurring bills.
  // Non-recurring bills skip the modal and delete immediately.
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Called by either the delete button (non-recurring path) or by the
  // ChooseDeleteScopeModal (recurring path) once the user has picked a scope.
  const performDelete = async (bill, deleteSeries) => {
    const rootId = bill.source_bill_id || bill.id;
    const backup = { ...bill };
    setBills((prev) => prev.filter((b) => {
      if (deleteSeries) return b.id !== rootId && b.source_bill_id !== rootId;
      return b.id !== bill.id;
    }));
    try {
      await api.delete(`/bills/${bill.id}${deleteSeries ? "?delete_series=true" : ""}`);
      toast(deleteSeries ? `Deleted "${bill.name}" series` : `Deleted "${bill.name}"`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data } = await api.post("/bills", {
                name: backup.name, due_date: backup.due_date,
                estimated_due_amount: backup.estimated_due_amount,
                paid_amount: backup.paid_amount, status: backup.status,
                is_recurring: !!backup.is_recurring,
                recurrence_frequency: backup.recurrence_frequency || null,
                notes: backup.notes || null,
                funding_source_id: backup.funding_source_id || null,
                is_autopay: !!backup.is_autopay,
                is_carryover: !!backup.is_carryover,
              });
              setBills((prev) => [...prev, data]);
              toast.success(deleteSeries ? "Restored (single instance — series deletion isn't fully undoable)" : "Restored");
            } catch (e) { toast.error(formatApiError(e)); }
          },
        },
      });
    } catch (e) {
      // Rollback optimistic delete on failure
      setBills((prev) => [...prev, backup]);
      toast.error(formatApiError(e));
    }
    refreshBillsBadge();
  };

  const remove = async (bill) => {
    // Recurring bills get an explicit 3-choice modal. Non-recurring go
    // straight to delete + Undo toast.
    if (bill.is_recurring) {
      setDeleteConfirm(bill);
      return;
    }
    await performDelete(bill, false);
  };

  const duplicate = async (bill) => {
    const { data } = await api.post(`/bills/${bill.id}/duplicate`);
    setBills((prev) => [...prev, data]);
    toast.success("Duplicated");
  };

  const rolloverRecurringToNextMonth = async () => {
    const monthlyRecurring = thisMonthBills.filter((b) => b.is_recurring && b.recurrence_frequency === "monthly");
    if (monthlyRecurring.length === 0) {
      toast.info("No monthly recurring bills to roll over.");
      return;
    }
    if (!confirm(`Create ${monthlyRecurring.length} recurring bill(s) for next month?`)) return;
    let created = 0;
    for (const b of monthlyRecurring) {
      const oldDate = new Date(b.due_date + "T00:00:00");
      const nextDate = new Date(oldDate.getFullYear(), oldDate.getMonth() + 1, oldDate.getDate());
      const already = nextMonthBills.find((n) => n.name === b.name);
      if (already) continue;
      try {
        const { data } = await api.post("/bills", {
          name: b.name,
          due_date: nextDate.toISOString().slice(0, 10),
          estimated_due_amount: b.estimated_due_amount,
          status: "unpaid", is_recurring: true, recurrence_frequency: "monthly",
          notes: b.notes || null, funding_source_id: b.funding_source_id || null,
          is_autopay: !!b.is_autopay, is_carryover: false,
        });
        setBills((prev) => [...prev, data]);
        created += 1;
      } catch { /* ignore */ }
    }
    toast.success(`Added ${created} bill(s) to next month`);
  };

  const hasPendingClearing = bills.some((b) => displayFor(b, today).notCleared);

  const commonRowProps = {
    today, ranges,
    onEdit: (b) => { setEditing(b); setShowModal(true); },
    onReschedule: (b) => setRescheduling(b),
    onSplit: (b) => setSplitting(b),
    onDelete: remove, onDup: duplicate,
    onStatus: setStatus, onAmount: updateAmount,
    selectedIds,
    onToggleSelect: (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]),
  };

  return (
    <div className="space-y-6" data-testid="bills-page">
      <BillsPageHeader
        activePayPeriod={activePayPeriod}
        navigate={navigate}
        onAddBill={() => { setEditing(null); setShowModal(true); }}
        hasPendingClearing={hasPendingClearing}
        filter={filter}
        onFilterChange={setFilter}
        accountFilter={accountFilter}
        onAccountFilterChange={setAccountFilter}
        fundingSources={fundingSources}
      />

      {/* Desktop-only Return-to-Dashboard link (Feb 2026 D4). Sits between
          the header and the horizon/table stack. Muted teal, discoverable
          but not attention-grabbing. Mobile users already have quick
          Dashboard access via the hamburger menu so we skip this there. */}
      <div className="hidden sm:block -mt-2">
        <button
          type="button"
          onClick={() => navigate("/app/dashboard")}
          className="inline-flex items-center gap-1.5 text-sm text-[#4A6148] hover:text-[#3B5B3E] transition-colors focus:outline-none"
          data-testid="bills-back-to-dashboard"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          <span className="underline decoration-dotted underline-offset-2">Return to Dashboard</span>
        </button>
      </div>

      {loading ? (
        <div className="text-[#59605C]">Loading…</div>
      ) : (
        <div className="space-y-8">
          {/* Horizon toggle — lets the user flip between rolling-day windows
              (Next 2 weeks / 30 / 90) and the classic by-month view. Hidden
              when a pay-period or ad-hoc date range deep link is active
              because those are already focused windows. */}
          {!activePayPeriod && !activeDateRange && (
            <HorizonToggle value={horizonMode} onChange={setHorizonModePersistent} />
          )}

          {/* Month strip nav — only shown in by-month mode */}
          {!activePayPeriod && !activeDateRange && horizonMode === "month" && (
            <MonthStripNav
              chips={monthChips}
              selectedKey={selectedMonthKey}
              onSelect={setSelectedMonthKey}
            />
          )}

          {activePayPeriod ? (
            <>
              <MonthTable
                label={`Bills in this pay period · ${formatDate(activePayPeriod.period_start)} → ${formatDate(activePayPeriod.period_end)}`}
                bills={payPeriodBills}
                {...commonRowProps}
                testId="pay-period"
                emptyText="No bills fall inside this pay period window."
              />
              <PayPeriodFooter period={activePayPeriod} bills={payPeriodBills} onReschedule={setRescheduling} />
            </>
          ) : activeDateRange ? (
            <MonthTable
              label={`Bills in this window · ${formatDate(activeDateRange.period_start)} → ${formatDate(activeDateRange.period_end)}`}
              bills={dateRangeBills}
              {...commonRowProps}
              testId="date-range"
              emptyText="No bills fall inside this window."
            />
          ) : selectedChip ? (
            <MonthTable
              label={`${selectedChip.label} — ${selectedChip.count} bill${selectedChip.count === 1 ? "" : "s"} · ${formatMoney(selectedChip.total)} total`}
              bills={selectedMonthBills}
              {...commonRowProps}
              testId="month-single"
              emptyText={`No bills recorded for ${selectedChip.label}.`}
            />
          ) : horizonMode !== "month" ? (
            <>
              <HorizonSummary
                days={horizonDays}
                bills={horizonBills}
                pastDueCount={horizonPastDue.length}
                pastDueTotal={horizonPastDueTotal}
                sort={horizonSort}
                onSortChange={setHorizonSortPersistent}
              />
              {showCushionWarning && (
                <div
                  className="card-soft !p-4 border border-[#E5B4A3] bg-[#FDF2F0]"
                  data-testid="bills-cushion-warning"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#7A2E24] text-white flex items-center justify-center shrink-0 font-heading font-bold">!</div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#7A2E24]">
                        Cushion warning
                      </div>
                      <div className="font-heading text-base font-semibold text-[#2C302E] mt-0.5">
                        Bills owed exceed your available money by {formatMoney(cushionShortfall)}
                      </div>
                      <div className="text-xs text-[#59605C] mt-1 leading-relaxed">
                        In the next {horizonDays} days you owe <strong>{formatMoney(horizonUnpaidTotal)}</strong>. You have <strong>{formatMoney(availableCash)}</strong> on hand{horizonIncoming > 0 ? <> plus <strong>{formatMoney(horizonIncoming)}</strong> in expected income arriving inside the window (total <strong>{formatMoney(totalAvailableInWindow)}</strong>)</> : ""}. Consider rescheduling a non-urgent bill or confirming that upcoming income lands on time.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <MonthTable
                label={`Bills due in the next ${horizonDays} days${horizonPastDue.length > 0 ? " (including past due)" : ""}`}
                bills={horizonBills}
                {...commonRowProps}
                testId="horizon-window"
                emptyText={
                  <div className="text-center">
                    Nothing due in the next {horizonDays} days.
                    <div className="text-xs text-[#8A918D] mt-1">
                      Try a longer horizon, or switch to &quot;By month&quot; to review past months.
                    </div>
                  </div>
                }
              />

              {/* Recently paid (last 30 days) — collapsible so it doesn't
                  crowd the primary horizon list, but always accessible from
                  the same page. Answers the mobile-first question "did I
                  already pay X this month?" without users having to leave
                  the current view or switch modes. */}
              {recentlyPaidBills.length > 0 && (
                <div className="card-soft !p-0 overflow-hidden" data-testid="bills-recently-paid-block">
                  <button
                    type="button"
                    onClick={() => setShowRecentlyPaid((v) => !v)}
                    className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#F5F0E4] transition-colors"
                    data-testid="bills-recently-paid-toggle"
                  >
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Recently paid · last 30 days</div>
                      <div className="font-heading text-base text-[#2C302E] mt-0.5">
                        {recentlyPaidBills.length} bill{recentlyPaidBills.length === 1 ? "" : "s"} · {formatMoney(recentlyPaidTotal)}
                      </div>
                    </div>
                    <span className={`text-[#8A918D] text-xs font-semibold transition-transform ${showRecentlyPaid ? "rotate-180" : ""}`}>▾</span>
                  </button>
                  {showRecentlyPaid && (
                    <div className="border-t border-[#EBE6E0]">
                      <MonthTable
                        label=""
                        bills={recentlyPaidBills}
                        {...commonRowProps}
                        testId="horizon-recently-paid"
                        emptyText="Nothing marked paid in the last 30 days."
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Wanting to look further back? Nudge toward the by-month
                  mode so users discover it without having to hunt. Only
                  shown when at least one recently-paid bill exists so the
                  hint feels contextual, not chatty. */}
              {recentlyPaidBills.length > 0 && (
                <div className="text-center text-xs text-[#8A918D] leading-relaxed" data-testid="bills-lookback-hint">
                  Looking for bills paid more than 30 days ago?{" "}
                  <button
                    type="button"
                    onClick={() => setHorizonModePersistent("month")}
                    className="font-semibold text-[#4A6148] underline decoration-dotted underline-offset-2 hover:text-[#3B5B3E]"
                    data-testid="bills-lookback-hint-link"
                  >
                    Switch to &ldquo;By month&rdquo; view →
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {carryoverBills.length > 0 && (
                <CarryoverPanel
                  bills={carryoverBills}
                  total={carryoverTotal}
                  {...commonRowProps}
                />
              )}

              <MonthTable
                label={`This month · ${monthLabel(today)}`}
                bills={thisMonthBills}
                {...commonRowProps}
                testId="month-current"
                emptyText="No bills this month. Add one above."
              />
              <MonthTable
                label={`Next month · ${monthLabel(nextMonth)}`}
                bills={nextMonthBills}
                {...commonRowProps}
                testId="month-next"
                emptyText={
                  <div className="flex flex-col items-center gap-3">
                    <div>No bills scheduled for next month yet.</div>
                    <button className="btn-secondary text-sm" onClick={rolloverRecurringToNextMonth} data-testid="bills-rollover-recurring">
                      <RefreshCw size={14} /> Roll over monthly recurring bills
                    </button>
                  </div>
                }
              />
              {otherMonthBills.length > 0 && (
                <MonthTable
                  label="Future bills (beyond next month)"
                  bills={otherMonthBills}
                  {...commonRowProps}
                  testId="month-other"
                  emptyText=""
                />
              )}
            </>
          )}
        </div>
      )}

      {showModal && (
        <BillModal
          bill={editing}
          fundingSources={fundingSources}
          existingBills={bills}
          onClose={() => setShowModal(false)}
          onDelete={editing ? (b) => { setShowModal(false); remove(b); } : null}
          onSaved={(updated) => {
            setShowModal(false);
            if (editing) {
              applyUpdatedBill(updated);
              // Recurring edits cascade name / amount / funding source /
              // autopay / pay url / notes / frequency to all future unpaid
              // rows in the chain. Refetch so the whole chain reflects the
              // change immediately, not just the edited row.
              if (updated.is_recurring || editing.is_recurring) refreshBills();
            } else {
              setBills((prev) => [...prev, updated]);
            }
          }}
        />
      )}

      {rescheduling && (
        <RescheduleBillModal
          bill={rescheduling}
          onClose={() => setRescheduling(null)}
          onUpdated={(b) => applyUpdatedBill(b)}
          onBulkUpdated={(rows, deletedIds) => {
            // Merge every returned row into local state so the whole
            // recurring chain reflects the new dates immediately. Also drop
            // any rows the backend deleted (scope=future collisions).
            setBills((prev) => {
              const deleted = new Set(deletedIds || []);
              const byId = new Map(prev.filter((b) => !deleted.has(b.id)).map((b) => [b.id, b]));
              rows.forEach((r) => byId.set(r.id, { ...(byId.get(r.id) || {}), ...r }));
              return Array.from(byId.values());
            });
          }}
        />
      )}

      {splitting && (
        <SplitBillModal
          bill={splitting}
          payPeriods={payPeriods}
          onClose={() => setSplitting(null)}
          onSplit={(orig, split) => {
            setBills((prev) => {
              const withUpdated = prev.map((b) => (b.id === orig.id ? { ...b, ...orig } : b));
              return [...withUpdated, split];
            });
            refreshBillsBadge();
            setSplitting(null);
          }}
        />
      )}

      {deleteConfirm && (
        <ChooseDeleteScopeModal
          bill={deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onChoose={async (scope) => {
            const bill = deleteConfirm;
            setDeleteConfirm(null);
            if (scope === "instance") await performDelete(bill, false);
            else if (scope === "series") await performDelete(bill, true);
            // scope === "cancel" is a no-op — modal is already closed.
          }}
        />
      )}

      {selectedIds.length > 0 && (
        <BulkActionsToolbar
          count={selectedIds.length}
          onClear={() => setSelectedIds([])}
          onBulkStatus={async (status) => {
            try {
              const { data } = await api.post("/bills/bulk-update", { bill_ids: selectedIds, status });
              const map = Object.fromEntries(data.updated.map((d) => [d.id, d]));
              setBills((prev) => prev.map((b) => (map[b.id] ? { ...b, ...map[b.id] } : b)));
              refreshBillsBadge();
              toast.success(`Marked ${data.count} bill${data.count === 1 ? "" : "s"} ${status}`);
              setSelectedIds([]);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          onBulkReschedule={async (dateStr) => {
            try {
              const { data } = await api.post("/bills/bulk-update", { bill_ids: selectedIds, reschedule_to: dateStr });
              const map = Object.fromEntries(data.updated.map((d) => [d.id, d]));
              setBills((prev) => prev.map((b) => (map[b.id] ? { ...b, ...map[b.id] } : b)));
              toast.success(`Rescheduled ${data.count} bill${data.count === 1 ? "" : "s"} to ${formatDate(dateStr)}`);
              setSelectedIds([]);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}
    </div>
  );
}
